import React, { FunctionComponent, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import classnames from 'classnames';
import withStyles from 'react-jss';

import { ClassProp } from 'helpers/types';
import FormError from '../FormError';
import { theme } from 'config';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  name: string;
  placeholder: string;
  label: string;
  size?: string;
  tabIndex?: number;
  disabled?: boolean;
  autoComplete?: string;
  validate?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

const styles = {
  container: {
    width: '100%',
    marginTop: '.25rem !important',
  },
  small: {
    width: '31%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    flex: '1 0 auto',
    maxWidth: '100%',
    width: '100%',
    textAlign: 'left',
    margin: '0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21428571rem !important',
    fontSize: '1rem !important',
    background: '#fff',
    color: 'rgba(0,0,0,.87)',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  disabled: {
    background: '#e1e8ed',
  },
  error: theme.error,
};

const emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const EmailInput: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  size,
  disabled = false,
  onChange,
  onKeyDown,
  autoComplete,
  validate,
  ...rest
}) => {
  const { register, setValue, trigger, errors } = useFormContext();

  useEffect(() => {
    register(name, {
      pattern: { value: emailRegExp, message: 'Malformed Email' },
      required: 'Email cannot be blank',
    });
  }, []);

  const changeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setValue(name, value);
    trigger(name);
  };

  const fieldErrors = errors[name];

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label htmlFor={name} />
      <span className={classes.fieldLabel}>{label}</span>
      <input
        className={classnames(classes.fieldInput, {
          [classes.error]: fieldErrors,
        })}
        id={name}
        name={name}
        type="email"
        onBlur={changeEvent}
        onChange={changeEvent}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoComplete={autoComplete}
        ref={register}
        {...rest}
      />

      {fieldErrors && <FormError name={name} errors={errors} />}
    </div>
  );
};

export default withStyles(styles)(EmailInput);
